import React, {ReactElement, useContext, useEffect, useState} from 'react';

import moment from 'moment-tz';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

import {ChatTextEdtionContext} from 'app/openai/types';
import {useHistoryData} from 'app/openai/utils';
import {useChatMessageSubscription} from 'app/subscriptions/hooks';
import {gt} from 'app/utils/i18n';
import {reportToSentry} from 'app/utils/sentry';
import {WebsocketContext} from 'app/ws/context';

import {type ChatMessageType} from './ChatMessage';
import {
  EditionHistoryPanel,
  type EditionHistoryType,
} from './EditionHistoryPanel';
import {MessagePanel} from './MessagePanel';

import {ProcedureVersionSerializedType} from 'sweet-types';

type Props = {
  title: string;
  text: string | null | undefined;
  isOpen: boolean;
  toggleModal: () => void;
  onConfirmText: (newText: string | ReactElement) => void;
  document?: ProcedureVersionSerializedType;
  context?: ChatTextEdtionContext;
};

export const EditTextWithOpenAIModal = ({
  text,
  title,
  isOpen,
  toggleModal,
  onConfirmText,
  document,
  context,
}: Props) => {
  if (!isOpen) {
    return null;
  }

  if (text == null) {
    return null;
  }
  const {sendWebsocketMessage, isWSOpen, isWSClosing} =
    useContext(WebsocketContext);

  const [editionHistory, appendEditionHistory] =
    useHistoryData<EditionHistoryType>();

  const [editedTextVersionIndex, setEditedTextVersionIndex] = useState(-1);

  const [messageHistory, appendMessageHistory] =
    useHistoryData<ChatMessageType>();

  useEffect(() => {
    if (isWSClosing && !['ready', 'error'].includes(chatStatus)) {
      // websocket just closed while we were using it!!
      setChatStatus('error');
      appendMessageHistory({
        source: 'error',
        text: "I'm sorry the connection with SweetAI has closed unexpectedly. Please try again later.",
      });
    }
  }, [isWSClosing]);

  useEffect(() => {
    appendMessageHistory({
      source: 'ai',
      text: `Hello! I'm here to help you refine this step "${title}". Let's work together to make it perfect. How can I assist you today?`,
    });
    setChatMessage('Hi SweetAI! Could you rewrite this step to be clearer?');
    appendEditionHistory({
      source: 'originalText',
      text,
      time: null,
    });
    setEditedTextVersionIndex(0);
  }, []);

  const editedText =
    editedTextVersionIndex > -1
      ? editionHistory[editedTextVersionIndex]
      : {
          source: 'originalText' as EditionHistoryType['source'],
          text,
          time: null,
        };

  const canShowPreviousVersion = () =>
    editedTextVersionIndex < editionHistory.length - 1;
  const canShowNextVersion = () => editedTextVersionIndex > 0;

  const showPreviousVersion = () => {
    if (canShowPreviousVersion()) {
      setEditedTextVersionIndex(editedTextVersionIndex + 1);
    }
  };

  const showNextVersion = () => {
    if (canShowNextVersion()) {
      setEditedTextVersionIndex(editedTextVersionIndex - 1);
    }
  };

  const [chatMessage, setChatMessage] = useState<string>('');

  const [chatStatus, setChatStatus] = useState<
    'ready' | 'sending' | 'processing' | 'error'
  >('ready');

  const [thread, setThread] = useState<string>();
  const shouldIgnoreWSResponses =
    chatStatus === 'ready' || chatStatus === 'error';
  const chatInitiated = thread != null;

  const processHeartbeat = (data) => {
    if (
      chatStatus === 'sending' &&
      (!chatInitiated || data.thread_id === thread)
    ) {
      setChatStatus('processing');
    }
  };

  const processError = (data) => {
    if (chatStatus === 'sending' || chatStatus === 'processing') {
      if (thread == data.thread_id || data.thread_id == null) {
        appendMessageHistory({source: 'error', text: data.message});
        setChatStatus('error');
      }
    }
  };

  const processWSResponse = (data) => {
    if (chatStatus === 'processing' && data.thread_id === thread) {
      try {
        const {message, text_to_edit: newText} = JSON.parse(data.message);
        appendMessageHistory({source: 'ai', text: message});
        if (newText != null) {
          appendEditionHistory({
            source: 'ai',
            text: newText,
            time: moment(),
          });
          setEditedTextVersionIndex(0); // The most recent message history is
          // added to the beginning of the array
        }
        setChatMessage('');
        setChatStatus('ready');
      } catch (error) {
        appendMessageHistory({
          source: 'error',
          text: gt(
            'Unable to process SweetAI response. Try repeating your last chat' +
              ' message'
          ),
        });
        setChatStatus('error');
        reportToSentry(error, undefined, false);
      }
    }
  };

  useChatMessageSubscription(
    (data) => {
      if (shouldIgnoreWSResponses) {
        // No need to listen for data changes if we don't even expect it
        return;
      }

      if (!chatInitiated) {
        setThread(data.thread_id);
      }

      if (data.type === 'heartbeat') {
        processHeartbeat(data);
      } else if (data.type === 'error') {
        processError(data);
      } else if (data.type === 'edit_text_chat') {
        processWSResponse(data);
      }
    },
    [thread, chatStatus]
  );

  const submitMessage = () => {
    if (!shouldIgnoreWSResponses) {
      return;
    }

    const data: any = {
      type: 'edit_text_chat',
      message: JSON.stringify({
        text_to_edit: editedText.text,
        message: chatMessage,
      }),
    };

    if (thread == null) {
      data.object_id = document?.id;
      data.content_type = document?.content_type;
      data.context = context;
    } else {
      data.thread_id = thread;
    }

    sendWebsocketMessage(
      JSON.stringify({
        event: 'chat.update',
        data,
      })
    );

    setChatStatus('sending');
    appendMessageHistory({source: 'me', text: chatMessage});
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClick={(e) => e.stopPropagation()}
      toggle={toggleModal}
      scrollable={true}
    >
      <ModalHeader>
        <i className="fas fa-robot" />
        <span className="m-2">
          {gt('Edit %s with SweetAI', <b>{title}</b>)}
        </span>
      </ModalHeader>
      <ModalBody className="px-0 pt-1 pb-0">
        <div className="container-fluid px-0">
          <div className="row no-gutters">
            <EditionHistoryPanel
              editedText={editedText}
              originalText={text}
              canShowNextVersion={canShowNextVersion()}
              canShowPreviousVersion={canShowPreviousVersion()}
              showNextVersion={showNextVersion}
              showPreviousVersion={showPreviousVersion}
            />
            <MessagePanel
              messageHistory={messageHistory}
              chatMessage={chatMessage}
              chatStatus={chatStatus}
              setChatMessage={setChatMessage}
              submitMessage={submitMessage}
              disabled={!isWSOpen}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={() => toggleModal()} className="btn btn-link btn-sm">
          <i className="fa fa-times" /> {gt('Cancel')}
        </button>
        <button
          disabled={text === editedText.text}
          onClick={() => {
            toggleModal();
            onConfirmText(editedText.text);
          }}
          className="btn btn-primary ml-1 d-block d-sm-inline-block"
        >
          <i className="fa fa-thumbs-up" /> {gt('Apply Changes')}
        </button>
      </ModalFooter>
    </Modal>
  );
};
